import {
    AccordionPanel,
    ChipAccordionPanel,
    NestedAccordionItem,
} from 'components/Accordion';
import DefaultFilterAccordionButton from './FilterAccordionButton';
import { PlaceholderText } from 'components/Placeholders';
import { handleFlavorFilterChange } from 'components/FlavorFilters/utils';
import kebabCase from 'lodash/kebabCase';
import styled from 'styled-components';
import { useUID } from 'react-uid';
import { withMixpanelChange } from 'utils/app';

const DefaultAccordionPanel = styled(AccordionPanel)`
    & > * {
        margin-bottom: ${({ theme }) => theme.space_2}px;
    }
`;

const DefaultChipAccordionPanel = styled(ChipAccordionPanel)`
    margin-top: 0;
    padding: ${({ theme }) => theme.space_7}px;
`;

const FilterAccordionNestedItemUntracked = ({
    children,
    panelId,
    abbrTitleHTML,
    label: itemLabel,
    options,
    isOpen,
    toggleOpen,
    appliedFilters,
    onChange,
    AccordionPanel = DefaultAccordionPanel,
    ChipAccordionPanel = DefaultChipAccordionPanel,
    FilterAccordionButton:
        FilterAccordionButtonProp = DefaultFilterAccordionButton,
    Icon,
    FilterOption,
    FilterChipItemButton,
    showChipsWhenClosed = true,
    space,
    filters,
    setFilter,
    catThreeFlavorCounts,
    $FilterAccordionNestedItemBgColor,
    groupedFlavorsOptions,
    isLoadingData,
}) => {
    const hasActiveFilters =
        !!appliedFilters &&
        appliedFilters.filter(
            (a) => options && options[0] && a.catTwo === options[0].catTwo,
        ).length > 0;
    const PanelComponent = isOpen ? AccordionPanel : ChipAccordionPanel;
    const panelProps = isOpen ? { $space: space } : {};
    const stableId = useUID();
    const sortedOptions = options?.sort((a, b) => {
        if (a.value === kebabCase(`all-${a.catTwo}`)) {
            return -1;
        }
        if (b.value === kebabCase(`all-${b.catTwo}`)) {
            return 1;
        }
        const countA =
            catThreeFlavorCounts && catThreeFlavorCounts[a.value]
                ? catThreeFlavorCounts[a.value]
                : 0;
        const countB =
            catThreeFlavorCounts && catThreeFlavorCounts[b.value]
                ? catThreeFlavorCounts[b.value]
                : 0;
        return countA > countB ? -1 : 1;
    });

    return (
        <NestedAccordionItem
            $FilterAccordionNestedItemBgColor={
                $FilterAccordionNestedItemBgColor
            }
            $isOpen={isOpen}
        >
            {FilterAccordionButtonProp && (
                <FilterAccordionButtonProp
                    onClick={(e) => toggleOpen()}
                    isActive={isOpen}
                    isOpen={isOpen}
                    Icon={Icon}
                    {...(isOpen && {
                        'aria-controls': `${stableId}-${panelId}`,
                    })}
                >
                    {abbrTitleHTML ? (
                        <span
                            dangerouslySetInnerHTML={{ __html: abbrTitleHTML }}
                        />
                    ) : (
                        <span>{itemLabel}</span>
                    )}
                </FilterAccordionButtonProp>
            )}
            {isOpen || (showChipsWhenClosed && hasActiveFilters) ? (
                <PanelComponent {...panelProps} id={`${stableId}-${panelId}`}>
                    {sortedOptions.map((option) => {
                        const { label, value } = option;
                        const count =
                            catThreeFlavorCounts &&
                            catThreeFlavorCounts[option.value];
                        const checked =
                            !!appliedFilters &&
                            !!(
                                appliedFilters.filter(
                                    (a) =>
                                        a.value === value ||
                                        kebabCase(
                                            `${a.catTwo}>${a.catThree}`,
                                        ) === value,
                                ).length > 0
                            );
                        return isOpen ? (
                            <FilterOption
                                checked={checked}
                                onChange={() =>
                                    handleFlavorFilterChange({
                                        option,
                                        filters,
                                        setFilter,
                                        groupedFlavorsOptions,
                                        isChecked: checked,
                                    })
                                }
                                {...option}
                                key={value}
                            >
                                {label}{' '}
                                {isLoadingData ? (
                                    <PlaceholderText
                                        css={'margin-left: .5rem'}
                                        width={'1.5rem'}
                                    />
                                ) : count ? (
                                    <span
                                        css={`
                                            font-weight: 500;
                                            margin-left: 0.5rem;
                                            color: ${({ theme }) =>
                                                theme.gray_600};
                                        `}
                                    >
                                        {count}
                                    </span>
                                ) : null}
                            </FilterOption>
                        ) : checked ? (
                            <FilterChipItemButton
                                onClick={() =>
                                    handleFlavorFilterChange({
                                        option,
                                        filters,
                                        setFilter,
                                        groupedFlavorsOptions,
                                        isChecked: true,
                                        isChip: true,
                                        onChange,
                                    })
                                }
                                {...option}
                                key={value}
                            >
                                {label}
                            </FilterChipItemButton>
                        ) : null;
                    })}
                </PanelComponent>
            ) : null}
            {isOpen ? children : null}
        </NestedAccordionItem>
    );
};

export const handleMixpanelEvent = ({ action, option }) => {
    switch (action) {
        case 'select-option':
        case 'remove-value':
            return {
                action,
                label: option.label,
                value: option.value,
            };
        default:
            return { action };
    }
};

const FilterAccordionNestedItemWithMixpanel = withMixpanelChange(
    FilterAccordionNestedItemUntracked,
    'FilterAccordionNestedItem',
);
const FilterAccordionNestedItem = ({
    handleMixpanelEvent: handleMixpanelEventDefault = handleMixpanelEvent,
    ...props
}) => {
    return (
        <FilterAccordionNestedItemWithMixpanel
            handleMixpanelEvent={handleMixpanelEventDefault}
            {...props}
        />
    );
};

export default FilterAccordionNestedItem;
